import React, { useState, useRef, useEffect } from 'react';
import './Impact.css';
import Header from '../components/Header';
import Footer from '../components/Footer';
import apiService from '../services/api';

// --- Reusable Components ---

function Reveal({ as: Tag = 'div', className = '', delay = 0, children, ...rest }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold: 0.1 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [ref]);
  return (
    <Tag
      ref={ref}
      className={`reveal ${visible ? 'reveal-visible' : ''} ${className}`}
      style={{ transitionDelay: visible ? `${delay}ms` : '0ms' }}
      {...rest}
    >
      {children}
    </Tag>
  );
}

// --- Modal Component ---
function StoryModal({ story, onClose }) {
  if (!story) return null;

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    } catch {
      return '';
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>
          <i className="fas fa-times"></i>
        </button>
        <div className="modal-body">
          {story.image && (
            <div className="modal-image">
              <img src={story.image} alt={story.name} loading="lazy" />
            </div>
          )}
          <div className="modal-info">
            {story.designation && (
              <span className="modal-category">{story.designation}</span>
            )}
            <h2 className="modal-name">{story.name}</h2>
            {story.company_name && (
              <p className="modal-company">
                <i className="fas fa-building"></i> {story.company_name}
              </p>
            )}
            <div className="modal-divider"></div>
            <p className="modal-description">{story.description}</p>
            {story.impact_code && (
              <p className="modal-code">
                <i className="fas fa-tag"></i> Story ID: {story.impact_code}
              </p>
            )}
            {story.created_at && (
              <p className="modal-code">
                <i className="far fa-calendar-alt"></i> Added: {formatDate(story.created_at)}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// --- Main Impact Component ---

export default function Impact() {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [successStories, setSuccessStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedStory, setSelectedStory] = useState(null);
  const [showModal, setShowModal] = useState(false);

  // Fetch success stories from API
  useEffect(() => {
    const fetchStories = async () => {
      setLoading(true);
      try {
        const result = await apiService.fetchSuccessStory();
        console.log('📊 Success stories response:', result);
        
        if (result && result.data) {
          let stories = Array.isArray(result.data) ? result.data : [result.data];
          
          // Filter: only show active and not deleted
          stories = stories.filter(item => 
            (item.status === 'active' || item.status === 1) && 
            (item.delete_status === 0 || item.delete_status === '0')
          );
          
          // Sort by id (newest first)
          stories.sort((a, b) => (b.id || 0) - (a.id || 0));
          
          setSuccessStories(stories);
        } else {
          setSuccessStories([]);
        }
      } catch (error) {
        console.error('❌ Error fetching success stories:', error);
        setSuccessStories([]);
      } finally {
        setLoading(false);
      }
    };
    fetchStories();
  }, []);

  // Get unique categories from data
  const getCategories = () => {
    const cats = ['all'];
    const uniqueCategories = [...new Set(successStories.map(item => item.designation).filter(Boolean))];
    return [...cats, ...uniqueCategories];
  };

  const categories = getCategories();

  // Filter stories
  const filteredStories = successStories.filter(item => {
    const matchesCategory = selectedCategory === 'all' || item.designation === selectedCategory;
    const matchesSearch = 
      (item.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (item.designation || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (item.company_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (item.description || '').toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  // Handle story click - open modal
  const handleStoryClick = (story) => {
    setSelectedStory(story);
    setShowModal(true);
    document.body.style.overflow = 'hidden';
  };

  // Close modal
  const closeModal = () => {
    setShowModal(false);
    document.body.style.overflow = 'auto';
  };

  // Close modal on Escape key
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') closeModal();
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, []);

  // IMPACT STATS
  const IMPACT_STATS = [
    { value: successStories.length > 0 ? `${successStories.length}+` : '100+', label: 'Success Stories', icon: 'fa-star' },
    { value: '5000+', label: 'Lives Transformed', icon: 'fa-users' },
    { value: '500+', label: 'Students Currently', icon: 'fa-graduation-cap' },
    { value: '36+', label: 'Years of Service', icon: 'fa-calendar' },
    { value: '300+', label: 'Awards & Accolades', icon: 'fa-trophy' },
    { value: '100%', label: 'Free Education', icon: 'fa-book-open' },
  ];

  return (
    <>
      <Header />
      <div className="impact-page devnar">
        <a className="skip-link" href="#main-impact">Skip to main content</a>

        {/* --- Page Hero --- */}
        <section className="page-hero-impact">
          <div className="hero-background">
            <div className="hero-overlay"></div>
            <div className="hero-pattern"></div>
          </div>
          <div className="wrap hero-content-impact">
            <Reveal>
              <span className="hero-badge">Our Impact</span>
              <h1>Success Stories</h1>
              <p className="hero-subtitle">
                Real stories of determination and achievement — our students overcoming 
                challenges and building successful careers.
              </p>
            </Reveal>
          </div>
        </section>

        <main id="main-impact" className="wrap page-content-impact">

          {/* --- Stats --- */}
          <section className="impact-stats">
            <div className="stats-grid">
              {IMPACT_STATS.map((stat, index) => (
                <Reveal key={stat.label} className="stat-card" delay={index * 60}>
                  <i className={`fas ${stat.icon} stat-icon`}></i>
                  <span className="stat-number">{stat.value}</span>
                  <span className="stat-label">{stat.label}</span>
                </Reveal>
              ))}
            </div>
          </section>

          {/* --- Introduction --- */}
          <section className="impact-intro">
            <Reveal className="section-title" as="div">
              <span className="eyebrow">Success Stories</span>
              <h2>Changing Lives, Creating Futures</h2>
              <div className="rule" />
              <p className="section-subtitle">
                We have showcased impact stories that have changed lives. There are over 100 stories 
                similar to these. We thank our donors and partners for making this possible.
              </p>
            </Reveal>
          </section>

          {/* --- Filter --- */}
          <div className="impact-filter-wrapper">
            <div className="filter-buttons">
              {categories.map((category) => (
                <button
                  key={category}
                  className={`filter-btn ${selectedCategory === category ? 'active' : ''}`}
                  onClick={() => setSelectedCategory(category)}
                >
                  {category === 'all' ? 'All Stories' : category}
                </button>
              ))}
            </div>
            <div className="search-box">
              <input
                type="text"
                placeholder="Search stories..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
              <i className="fas fa-search search-icon"></i>
            </div>
          </div>

          {/* --- Stories Grid --- */}
          <section className="stories-grid-section">
            {loading ? (
              <div className="loading-container">
                <div className="loading-spinner"></div>
                <p>Loading success stories...</p>
              </div>
            ) : filteredStories.length === 0 ? (
              <div className="no-results">
                <i className="fas fa-book-open no-results-icon"></i>
                <h3>No Stories Found</h3>
                <p>Try adjusting your search or filter criteria.</p>
              </div>
            ) : (
              <div className="stories-grid">
                {filteredStories.map((story, index) => (
                  <Reveal 
                    key={story.id || index} 
                    className="story-card" 
                    delay={(index % 12) * 50}
                    onClick={() => handleStoryClick(story)}
                  >
                    {story.image && (
                      <div className="story-image">
                        <img src={story.image} alt={story.name} loading="lazy" />
                        {story.designation && (
                          <span className="story-category">{story.designation}</span>
                        )}
                      </div>
                    )}
                    <div className="story-content">
                      <span className="story-name">{story.name}</span>
                      {story.designation && (
                        <h4 className="story-designation">{story.designation}</h4>
                      )}
                      {story.company_name && (
                        <p className="story-company">
                          <i className="fas fa-building"></i> {story.company_name}
                        </p>
                      )}
                      <p className="story-description">
                        {story.description && story.description.length > 120 
                          ? `${story.description.substring(0, 120)}...` 
                          : story.description}
                      </p>
                      <div className="story-divider"></div>
                      <span className="story-read-more">
                        <i className="fas fa-arrow-right"></i> Read Full Story
                      </span>
                    </div>
                  </Reveal>
                ))}
              </div>
            )}
          </section>

          {/* --- Impact Highlights --- */}
          <section className="impact-highlights">
            <Reveal className="section-title" as="div">
              <span className="eyebrow">Key Impact Areas</span>
              <h2>Where We Make a Difference</h2>
              <div className="rule" />
            </Reveal>

            <div className="highlights-grid">
              <Reveal className="highlight-card" delay={0}>
                <i className="fas fa-graduation-cap highlight-icon"></i>
                <h3>Education</h3>
                <p>Free quality education from Nursery to Junior College, with innovative teaching methods tailored for visually challenged students.</p>
              </Reveal>
              <Reveal className="highlight-card" delay={80}>
                <i className="fas fa-home highlight-icon"></i>
                <h3>Boarding & Care</h3>
                <p>Free, safe residential facilities with nutritious meals, providing a nurturing home environment for over 670 students.</p>
              </Reveal>
              <Reveal className="highlight-card" delay={160}>
                <i className="fas fa-laptop highlight-icon"></i>
                <h3>Technology & Skills</h3>
                <p>Pioneering computer education, assistive technology, and vocational training for independent living and employment.</p>
              </Reveal>
              <Reveal className="highlight-card" delay={240}>
                <i className="fas fa-trophy highlight-icon"></i>
                <h3>National Recognition</h3>
                <p>Honoured by four Presidents, the Vice President, and the Prime Minister of India for exceptional service.</p>
              </Reveal>
              <Reveal className="highlight-card" delay={320}>
                <i className="fas fa-globe-asia highlight-icon"></i>
                <h3>Global Reach</h3>
                <p>Students from 14 countries, international volunteers, and global recognition for our innovative approach.</p>
              </Reveal>
              <Reveal className="highlight-card" delay={400}>
                <i className="fas fa-briefcase highlight-icon"></i>
                <h3>Employment Success</h3>
                <p>Over 100 success stories of graduates working in top companies, banks, and government institutions across India.</p>
              </Reveal>
            </div>
          </section>

          {/* --- CTA Section --- */}
          <Reveal as="section" className="cta-band-impact">
            <div className="cta-content">
              <span className="cta-eyebrow">Be a Part of the Impact</span>
              <h2>Help Us Create More Stories</h2>
              <p>
                Every contribution helps us transform more lives. Join us in empowering 
                visually challenged children to achieve their dreams.
              </p>
              <div className="cta-buttons">
                <a className="btn btn-primary" href="/sponsor-donation">
                  <i className="fas fa-heart"></i> Support Our Mission
                </a>
                <a className="btn btn-outline btn-outline-light" href="/volunteer">
                  <i className="fas fa-hands-helping"></i> Become a Volunteer
                </a>
              </div>
            </div>
          </Reveal>

        </main>
      </div>

      {/* Story Modal */}
      <StoryModal story={selectedStory} onClose={closeModal} />

      <Footer />
    </>
  );
}